/**
 * +glide: Space as jump-and-glide (BotW / Genshin): a press on the ground jumps; held past the hop's
 * apex it opens the glider; a press mid-air opens it at once; letting go folds it. Pure (no DOM, no
 * clock): the session feeds it the key edges and, every frame, the own hero's air state.
 */
import type { DeepReadonly, Unit } from '@keystone/sim';
import { JUMP_MS } from '@keystone/sim';

export interface AirView {
  /** In the air (a hop, a fall, a glide). */
  airborne: boolean;
  /** Past the hop's apex (or in free flight already). */
  pastApex: boolean;
  gliding: boolean;
}

/** The own hero's air state at sim time `timeMs`. */
export function airView(u: DeepReadonly<Pick<Unit, 'jump'>> | undefined, timeMs: number): AirView {
  const j = u?.jump;
  if (!j) return { airborne: false, pastApex: false, gliding: false };
  return { airborne: true, pastApex: !!j.air || timeMs >= j.at + JUMP_MS / 2, gliding: !!j.air?.glide };
}

export class GlideHold {
  private held = false;
  /** A glide-on went out for this hold (so the release folds it). */
  private asked = false;

  get isHeld(): boolean {
    return this.held;
  }

  /** Space went down. True: ask for the glider (a press mid-air); false: jump. */
  press(airborne: boolean): boolean {
    this.held = true;
    this.asked = airborne;
    return airborne;
  }

  /** Space came up. True: fold the glider. */
  release(gliding: boolean): boolean {
    const fold = this.held && (this.asked || gliding);
    this.held = false;
    this.asked = false;
    return fold;
  }

  /** Every frame while held. True (once per hold): the hop passed its apex with Space still down: open the glider. */
  poll(v: AirView): boolean {
    if (!this.held || this.asked || !v.airborne || v.gliding || !v.pastApex) return false;
    this.asked = true;
    return true;
  }

  /** Focus lost / input disabled: forget the key (no fold sent: the glider stays as it is). */
  reset(): void {
    this.held = false;
    this.asked = false;
  }
}
